import { useEffect, useRef, useState } from "react";
import { Button, Dropdown, Icon, IconButton, Input, Popover } from "../..";
import "./ChatInput.css";
import { useTranslation } from "react-i18next";

const ChatInput = ({
  onSend,
  isLoading,
  className,
  showFileDropdown = false,
  onAbort,
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const fileInputRef = useRef(null);

  const [inputValue, setInputValue] = useState("");
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!isLoading) {
      ref?.current?.focus?.();
    }
  }, [isLoading]);

  useEffect(() => {
    resizeTextarea();
  }, [inputValue]);

  const resizeTextarea = () => {
    const textarea = ref.current;
    if (textarea) {
      textarea.style.height = "auto";
      const newHeight = Math.min(textarea.scrollHeight, 100); // Cap to 100px
      textarea.style.height = `${newHeight + 2}px`;
    }
  };

  const handleSend = () => {
    if (!inputValue.trim().length) return;
    if (!(inputValue?.length > 0)) return;
    onSend({
      message: inputValue,
      files: files,
    });
    setInputValue("");
    setFiles([]);
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length) {
      setFiles(selectedFiles);
      e.target.value = "";
    }
  };

  const dropdownOptions = [
    {
      label: t("Upload file"),
      value: "upload-file",
      icon: <Icon icon="attachment" />,
    },
  ];

  return (
    <>
      {files.length > 0 && (
        <div className="chat-input__selected-files">
          {files.map((file, idx) => (
            <span key={file.name + idx} className="fileContainer">
              <Icon icon="file" /> {file.name}
              <IconButton
                icon={<Icon icon="close" />}
                onClick={() => setFiles(files.filter((_, i) => i !== idx))}
                size="xs"
                appearance="subtle"
                className="closeButton"
              />
            </span>
          ))}
        </div>
      )}
      <div className={`chat-input__root ${className}`}>
        <input
          type="file"
          multiple
          accept=".c,.cpp,.cs,.css,.doc,.docx,.go,.html,.java,.js,.json,.md,.pdf,.php,.pptx,.py,.rb,.sh,.tex,.ts,.txt"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        {showFileDropdown && (
          <Popover
            placement="topStart"
            trigger="click"
            content={
              <div
                className="chat-input__upload-link"
                style={{ cursor: "pointer", padding: 8 }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Icon icon="attachment" />{" "}
                {t("Upload file (.doc,.docx,.pdf,.pptx,.txt)")}
              </div>
            }
          >
            <IconButton
              icon={<Icon icon="plus" />}
              appearance="default"
              className="plusButton"
            />
          </Popover>
        )}
        <Input
          className={`chat-input__auto-resize-input`}
          inputRef={ref}
          componentClass="textarea"
          autoResize
          value={inputValue}
          placeholder={t("Ask anything")}
          onChange={(e) => setInputValue(e)}
          disabled={isLoading}
          rows="1"
          onKeyDown={(e) => {
            if (e.code === "Enter" && !e.nativeEvent.shiftKey) {
              e.preventDefault();
              return;
            }
          }}
          onKeyUp={(e) => {
            if (e.code === "Enter" && !e.nativeEvent.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />

        {(!isLoading || !onAbort) && (
          <Button
            appearance="primary"
            onClick={handleSend}
            disabled={isLoading || !(inputValue?.trim()?.length > 0)}
          >
            {t("Send")}{" "}
            <Icon icon="send" className={`chat-input__icon-button`} />
          </Button>
        )}

        {onAbort && isLoading && (
          <Button appearance="primary" onClick={onAbort}>
            <Icon icon="square" />
          </Button>
        )}
      </div>
    </>
  );
};

export default ChatInput;
